import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { extractError } from "../lib/api";
import { downloadApiFile } from "../lib/downloadApiFile";

const EMPTY_FORM = {
  from_date: "",
  to_date: "",
  code: "",
  purchase_code: "",
  party_name: "",
  variety: "",
};

function buildExportParams(form) {
  const params = { ordering: "-id" };
  if (form.from_date) params.from_date = form.from_date;
  if (form.to_date) params.to_date = form.to_date;
  if (form.code?.trim()) params.code = form.code.trim();
  if (form.purchase_code?.trim()) params.purchase_code = form.purchase_code.trim();
  if (form.party_name?.trim()) params.party_name = form.party_name.trim();
  if (form.variety?.trim()) params.variety = form.variety.trim();
  return params;
}

function formFromDefaults(defaults = {}) {
  return {
    ...EMPTY_FORM,
    from_date: defaults.from_date || "",
    to_date: defaults.to_date || "",
    code: defaults.code || "",
    purchase_code: defaults.purchase_code || "",
    party_name: defaults.party_name || "",
    variety: defaults.variety || "",
  };
}

export default function VoucherExportDialog({
  defaultFilters = {},
  filename = "vouchers.xlsx",
  testId = "vouchers-export",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => formFromDefaults(defaultFilters));

  useEffect(() => {
    if (!open) return;
    setForm(formFromDefaults(defaultFilters));
  // Pre-fill from table filters each time the dialog opens
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleExport = async () => {
    if (form.from_date && form.to_date && form.from_date > form.to_date) {
      toast.error("From date must be on or before To date");
      return;
    }
    setBusy(true);
    try {
      await downloadApiFile("/vouchers/export/excel", filename, buildExportParams(form));
      toast.success("Excel download started");
      setOpen(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleClear = () => setForm({ ...EMPTY_FORM });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1"
          data-testid={testId}
        >
          <Download className="h-3.5 w-3.5" />
          Export Excel
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle>Export vouchers to Excel</DialogTitle>
          <DialogDescription>
            Choose filters for the download. Leave fields blank to include all vouchers (up to 10,000 rows).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div>
            <Label className="text-xs">From date</Label>
            <Input
              type="date"
              className="mt-1 h-9 text-sm"
              value={form.from_date}
              onChange={(e) => set("from_date", e.target.value)}
              data-testid={`${testId}-from-date`}
            />
          </div>
          <div>
            <Label className="text-xs">To date</Label>
            <Input
              type="date"
              className="mt-1 h-9 text-sm"
              value={form.to_date}
              onChange={(e) => set("to_date", e.target.value)}
              data-testid={`${testId}-to-date`}
            />
          </div>
          <div>
            <Label className="text-xs">Voucher no.</Label>
            <Input
              className="mt-1 h-9 text-sm"
              placeholder="e.g. VCH-"
              value={form.code}
              onChange={(e) => set("code", e.target.value)}
              data-testid={`${testId}-code`}
            />
          </div>
          <div>
            <Label className="text-xs">Purchase code</Label>
            <Input
              className="mt-1 h-9 text-sm"
              placeholder="e.g. PUR-"
              value={form.purchase_code}
              onChange={(e) => set("purchase_code", e.target.value)}
              data-testid={`${testId}-purchase-code`}
            />
          </div>
          <div>
            <Label className="text-xs">Party name</Label>
            <Input
              className="mt-1 h-9 text-sm"
              placeholder="Farmer / supplier"
              value={form.party_name}
              onChange={(e) => set("party_name", e.target.value)}
              data-testid={`${testId}-party-name`}
            />
          </div>
          <div>
            <Label className="text-xs">Variety</Label>
            <Input
              className="mt-1 h-9 text-sm"
              placeholder="e.g. Parmal"
              value={form.variety}
              onChange={(e) => set("variety", e.target.value)}
              data-testid={`${testId}-variety`}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" size="sm" onClick={handleClear} disabled={busy}>
            Clear filters
          </Button>
          <Button type="button" size="sm" onClick={handleExport} disabled={busy} data-testid={`${testId}-submit`}>
            {busy ? "Exporting…" : "Download Excel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
